import { useClassPerfOverview } from '../../../api/hooks';
import { PanelCard, StatTile, StatGrid, Button, Chip, Term } from '../../../components/ui';
import HistogramChart from '../../../components/charts/HistogramChart';
import Spinner from '../../../components/ui/Spinner';
import ErrorPanel from '../../../components/ui/ErrorPanel';
import EmptyState from '../../../components/ui/EmptyState';
import { FaFileAlt } from '../../../components/ui/icons';
import { Key, Legend } from '../lib/cpBits';
import { priorDelta, formatPtsDelta, ptsTone, belowPassCount, spreadSentence, boxPlotGeometry } from '../lib/overviewMath';
import { PASS_MARK } from '../../../lib/tableRules';

interface Props {
  gradeId: number;
  sectionId: number | 'all';
  subjectId: number | 'all';
  weeks?: number;
  /** "Grade 10 · all sections", for the hint under the title. */
  ctx?: string;
  /** "Mathematics" or "All subjects", for the hint under the title. */
  subjLabel?: string;
  /** Switches the page to the Chapters tab. */
  onSeeChapters?: () => void;
}

const fmtPct = (v: number | null) => v == null ? '—' : `${v.toFixed(1)}%`;
const fmtNum = (v: number) => v.toString();

export default function OverviewTab({ gradeId, sectionId, subjectId, weeks, ctx, subjLabel, onSeeChapters }: Props) {
  const q = useClassPerfOverview({
    grade: gradeId, section: sectionId, subject: subjectId, weeks,
  });
  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  const d = q.data;
  if (d.students_with_data === 0) {
    return (
      <PanelCard>
        <EmptyState
          icon={<FaFileAlt />}
          title="No data yet"
          body="No exam attempts in this window yet."
        />
      </PanelCard>
    );
  }
  const total = d.histogram.reduce((n, b) => n + b.count, 0);
  const below = belowPassCount(d.histogram, PASS_MARK);
  const hasBand = d.p25 != null && d.p75 != null;
  const box = boxPlotGeometry(d.p25, d.median_score, d.p75);
  const delta = priorDelta(d.avg_score, d.prior_avg_score);
  const hint = [ctx, subjLabel, `last ${weeks ?? 12} weeks`].filter(Boolean).join(' · ');

  return (
    <>
      <PanelCard
        title="Cohort at a glance"
        align="end"
        subtitle={hint}
        actions={delta != null ? (
          <Chip tone={ptsTone(delta)}>{formatPtsDelta(delta)} pts vs prior window</Chip>
        ) : undefined}
      >
        <StatGrid>
          <StatTile label="Cohort" value={fmtNum(d.cohort_size)} sub="students enrolled" help="cohort" />
          <StatTile label="Students with data" value={fmtNum(d.students_with_data)}
            sub={d.cohort_size > 0 ? `${Math.round((d.students_with_data / d.cohort_size) * 100)}% of cohort` : undefined}
            help="students_with_data" />
          <StatTile label="Attempts" value={fmtNum(d.attempts_count)} sub="exam attempts in window" help="attempts" />
          <StatTile label="Avg score" value={fmtPct(d.avg_score)} tone="navy"
            sub={delta != null ? `${formatPtsDelta(delta)} pts vs prior` : undefined} help="avg_score" />
          <StatTile label="Median" value={fmtPct(d.median_score)} sub="middle student" help="median" />
          <StatTile label="Lower quartile" value={fmtPct(d.p25)} sub="P25" help="p25" />
          <StatTile label="Upper quartile" value={fmtPct(d.p75)} sub="P75" help="p75" />
        </StatGrid>

        <div className="mt-[22px] grid gap-[18px] min-[821px]:grid-cols-12">
          <div className="min-w-0 min-[821px]:col-span-6 min-[1181px]:col-span-7">
            <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-display text-[15px] font-bold tracking-[-0.01em] text-ink"><Term k="score_distribution">Score distribution</Term></h3>
                <p className="mt-1 text-[13px] text-muted">Students by average score, 10-point buckets</p>
              </div>
              <Legend>
                <Key tone="accent">{`Below ${PASS_MARK}%`}</Key>
                <Key tone="slate">{`${PASS_MARK}% and up`}</Key>
              </Legend>
            </div>
            <HistogramChart buckets={d.histogram} height={190} passPct={PASS_MARK} />
          </div>

          {hasBand && (
            <div className="min-w-0 rounded-[16px] bg-panel p-[18px] min-[821px]:col-span-6 min-[1181px]:col-span-5">
              <h3 className="font-display text-[15px] font-bold tracking-[-0.01em] text-ink">Where the middle half sits</h3>
              <p className="mt-1 text-[13px] text-muted">{spreadSentence(d.p25!, d.p75!)}</p>
              <div role="img" aria-label={`Middle half of scores between ${fmtPct(d.p25)} and ${fmtPct(d.p75)}`}
                className="relative mb-2 mt-[26px] h-11">
                <div className="absolute inset-x-0 top-[21px] h-0.5 rounded-sm bg-line-strong" />
                <div className="absolute top-2 h-7 rounded-[8px] bg-slate/40 dark:bg-slate/70" style={{ left: box.left, width: box.width }} />
                <div aria-hidden className="absolute -bottom-1.5 -top-1.5 border-l-2 border-dashed border-accent-ink" style={{ left: `${PASS_MARK}%` }} />
                <span aria-hidden className="absolute -top-6 -translate-x-1/2 whitespace-nowrap font-mono text-[11px] font-semibold text-accent-ink" style={{ left: `${PASS_MARK}%` }}>Pass {PASS_MARK}</span>
                {box.median != null && (
                  <div className="absolute top-0.5 -ml-px h-10 w-[3px] rounded-sm bg-navy dark:bg-mist-fixed" style={{ left: box.median }} />
                )}
              </div>
              <div className="flex justify-between font-mono text-[11px] font-medium text-muted" aria-hidden>
                {[0, 25, 50, 75, 100].map((t) => <span key={t}>{t}</span>)}
              </div>
              <div className="mt-[18px] grid grid-cols-3 gap-2.5">
                {[['P25', d.p25], ['Median', d.median_score], ['P75', d.p75]].map(([label, v]) => (
                  <div key={label as string} className="rounded-[12px] bg-surface px-3 py-2.5">
                    <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{label as string}</span>
                    <b className="mt-0.5 block font-mono text-[18px] font-semibold">{v == null ? '—' : (v as number).toFixed(1)}</b>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2.5">
                <div className="flex-1 rounded-[12px] bg-accent-bg px-3 py-2.5"
                  title={total > 0 ? `${Math.round((below / total) * 100)}% of ${total} scores` : undefined}>
                  <span className="text-xs font-semibold text-accent-ink">Below pass</span>
                  <b className="mt-0.5 block font-mono text-[18px] font-medium">{below}</b>
                </div>
                {onSeeChapters && (
                  <Button variant="secondary" size="sm" className="flex-1 !rounded-[12px]" onClick={onSeeChapters}>See weak chapters</Button>
                )}
              </div>
            </div>
          )}
        </div>
      </PanelCard>
    </>
  );
}
